/**
 * Sub-pass 2B Item 3 — Mandatory one-time broker survey modal.
 *
 * Non-dismissible dialog shown to every authenticated user who has
 * not yet completed broker-survey version 1. Backend is the source
 * of truth for completion — this component NEVER reads the local
 * survey state. Reuses existing shadcn primitives so the visual
 * language stays consistent with the rest of Stockaily.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Search, ShieldCheck, LogOut } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

const NO_BROKER_ID = "no_broker";
const OTHER_ID = "other";

export default function BrokerSurveyModal({ onCompleted }) {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const [catalogue, setCatalogue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [otherText, setOtherText] = useState("");
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const dialogRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/broker-survey/catalogue");
        if (!cancelled) setCatalogue(res?.data?.items || []);
      } catch (_) {
        if (!cancelled) setCatalogue([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Focus trap + block scroll while the modal is open. The dialog is
  // non-dismissible on purpose — no ESC handling, no backdrop close.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalogue;
    return catalogue.filter((row) => (row.label || "").toLowerCase().includes(q));
  }, [catalogue, search]);

  const toggle = (id) => {
    setError("");
    setSelected((prev) => {
      const next = new Set(prev);
      if (id === NO_BROKER_ID) {
        if (next.has(NO_BROKER_ID)) {
          next.delete(NO_BROKER_ID);
        } else {
          next.clear();
          next.add(NO_BROKER_ID);
          setOtherText("");
        }
        return next;
      }
      // Selecting any broker or Other clears the no-broker option.
      next.delete(NO_BROKER_ID);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      if (id === OTHER_ID && !next.has(OTHER_ID)) setOtherText("");
      return next;
    });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    setError("");
    if (selected.size === 0) {
      setError(t("broker_survey.err_required"));
      return;
    }
    if (selected.has(OTHER_ID) && !otherText.trim()) {
      setError(t("broker_survey.err_other_required"));
      return;
    }
    setSubmitting(true);
    const body = {
      broker_ids: Array.from(selected),
      other_text: selected.has(OTHER_ID) ? otherText.trim() : undefined,
      version: 1,
    };
    try {
      await api.post("/broker-survey", body);
      onCompleted?.();
    } catch (ex) {
      const code = ex?.response?.data?.detail?.code;
      if (code === "no_broker_exclusive") {
        setError(t("broker_survey.err_no_broker_exclusive"));
      } else if (code === "other_text_required") {
        setError(t("broker_survey.err_other_required"));
      } else if (code === "empty_selection") {
        setError(t("broker_survey.err_required"));
      } else {
        setError(t("broker_survey.err_generic"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="broker-survey-title"
      data-testid="broker-survey-modal"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => { if (e.key === "Escape") e.preventDefault(); }}
      ref={dialogRef}
    >
      <div className="w-full max-w-xl rounded-2xl border border-border/70 bg-card shadow-xl overflow-hidden">
        <div className="px-6 pt-6 pb-3 border-b border-border/60">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <ShieldCheck className="w-3 h-3" />
            <span>Stockaily</span>
          </div>
          <h2 id="broker-survey-title" className="mt-2 font-display text-2xl text-foreground" data-testid="broker-survey-title">
            {t("broker_survey.title")}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("broker_survey.helper")}</p>
          <p className="mt-2 text-xs text-muted-foreground/90 leading-relaxed">
            {t("broker_survey.explanation")}
          </p>
        </div>

        <form onSubmit={submit}>
          <div className="px-6 py-4 max-h-[52vh] overflow-y-auto space-y-3" data-testid="broker-survey-body">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("broker_survey.search_placeholder")}
                className="pl-9 h-10"
                data-testid="broker-survey-search"
                aria-label={t("broker_survey.search_placeholder")}
              />
            </div>

            {loading ? (
              <div className="flex justify-center py-8" data-testid="broker-survey-loading">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="grid gap-2 sm:grid-cols-2">
                  {filtered.map((row) => (
                    <BrokerRow
                      key={row.id}
                      id={row.id}
                      label={row.label}
                      checked={selected.has(row.id)}
                      onToggle={() => toggle(row.id)}
                    />
                  ))}
                </div>
                {/* Special options */}
                <div className="pt-2 border-t border-border/40 space-y-2">
                  <BrokerRow
                    id={OTHER_ID}
                    label={t("broker_survey.other_label")}
                    checked={selected.has(OTHER_ID)}
                    onToggle={() => toggle(OTHER_ID)}
                    testIdSuffix="other"
                  />
                  {selected.has(OTHER_ID) && (
                    <Input
                      type="text"
                      maxLength={80}
                      value={otherText}
                      onChange={(e) => setOtherText(e.target.value)}
                      placeholder={t("broker_survey.other_placeholder")}
                      className="h-10"
                      data-testid="broker-survey-other-input"
                      aria-label={t("broker_survey.other_placeholder")}
                    />
                  )}
                  <BrokerRow
                    id={NO_BROKER_ID}
                    label={t("broker_survey.no_broker_label")}
                    checked={selected.has(NO_BROKER_ID)}
                    onToggle={() => toggle(NO_BROKER_ID)}
                    testIdSuffix="no-broker"
                  />
                </div>
                <p className="text-[11px] text-muted-foreground/80">{t("broker_survey.multi_note")}</p>
              </>
            )}
          </div>

          <div className="px-6 py-4 border-t border-border/60 bg-muted/20 space-y-3">
            {error ? (
              <div className="text-xs text-[#E5484D]" data-testid="broker-survey-error">{error}</div>
            ) : null}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <a
                href="/legal/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                data-testid="broker-survey-privacy-link"
              >
                {t("broker_survey.privacy_link")}
              </a>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={async () => { try { await logout?.(); } catch { /* ignore */ } }}
                  data-testid="broker-survey-logout"
                >
                  <LogOut className="w-3.5 h-3.5 mr-1" />
                  {t("broker_survey.logout")}
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || loading}
                  data-testid="broker-survey-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      {t("broker_survey.submitting")}
                    </>
                  ) : t("broker_survey.continue")}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function BrokerRow({ id, label, checked, onToggle, testIdSuffix }) {
  return (
    <label
      className="flex items-center gap-2 rounded-md border border-border/50 bg-background/80 px-3 py-2 cursor-pointer hover:border-border transition-colors"
      data-testid={testIdSuffix ? `broker-option-${testIdSuffix}` : `broker-option-${id}`}
    >
      <Checkbox checked={checked} onCheckedChange={onToggle} aria-label={label} />
      <span className="text-sm text-foreground">{label}</span>
    </label>
  );
}
